import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import TimeRangeControl from './TimeRangeControl';
import PeriodToggleControl from './PeriodToggleControl';
import PlaceholderControl from './PlaceholderControl';
import type { DashboardControl } from '@/types/dashboard';

interface ControlBarProps {
  controls: DashboardControl[];
  activeControlValues: Record<string, unknown>;
  isRefreshing: boolean;
  onChangeControlValue: (parameterKey: string, value: unknown) => void;
}

const ControlBar: React.FC<ControlBarProps> = ({
  controls,
  activeControlValues,
  isRefreshing,
  onChangeControlValue,
}) => {
  const sortedControls = useMemo(
    () => [...controls].sort((a, b) => a.position - b.position),
    [controls]
  );

  if (sortedControls.length === 0) return null;

  return (
    <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border bg-background flex-wrap">
      {sortedControls.map((control) => {
        const currentValue = activeControlValues[control.parameter_key];

        switch (control.control_type) {
          case 'time_range_selector':
            return (
              <TimeRangeControl
                key={control.id}
                control={control}
                currentValue={currentValue}
                onChange={onChangeControlValue}
              />
            );
          case 'period_toggle':
            return (
              <PeriodToggleControl
                key={control.id}
                control={control}
                currentValue={currentValue}
                onChange={onChangeControlValue}
              />
            );
          default:
            return (
              <PlaceholderControl key={control.id} control={control} />
            );
        }
      })}

      {isRefreshing && (
        <div className="flex items-center gap-1.5 ml-auto text-xs text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>Updating...</span>
        </div>
      )}
    </div>
  );
};

export default ControlBar;
